import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import {
  Autocomplete,
  Chip,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { Methods } from "enums/Methods";
import {
  LookupLabwareType,
  Maybe,
  PlatesCreated,
} from "graphql/generated/schema-types";
import { useMemo, useState } from "react";
import { GenericFieldProps } from "./FeedGroupFormRow";
import { GenerateMystRack } from "./GenerateMystRack";

interface FeedGroupFormRowPlatesProps extends GenericFieldProps {
  value: number[];
  method: Methods;
  generateMystRack: boolean | undefined | null;
  setFieldValue: (
    field: string,
    value: string | number | number[] | boolean | undefined,
  ) => void;
  plates: Maybe<
    {
      __typename?: "PlatesCreated";
    } & Pick<
      PlatesCreated,
      | "plateCreatedId"
      | "plateBarcode"
      | "comments"
      | "dateCreated"
      | "labwareTypeId"
    > & {
        labwareType?: Maybe<
          {
            __typename?: "LookupLabwareType";
          } & Pick<LookupLabwareType, "plateLayoutTypeId">
        >;
      }
  >[];
}

export const FeedGroupFormRowPlates = ({
  index,
  method,
  value,
  generateMystRack,
  formikErrors,
  handleBlur,
  setFieldValue,
  toggleDisabled,
  plates,
}: FeedGroupFormRowPlatesProps) => {
  const [textAreaContent, setTextAreaContent] = useState<string>("");
  const [platesNotFound, setPlatesNotFound] = useState<string[]>([]);

  const availablePlates = useMemo(() => {
    if (method === Methods.EB_Feed) {
      return plates?.filter((plate) => plate?.labwareTypeId === 18) ?? [];
    } else if (method === Methods.Media_Exchange_12_24w) {
      return (
        plates?.filter(
          (plate) =>
            plate?.labwareType?.plateLayoutTypeId === 2 ||
            plate?.labwareType?.plateLayoutTypeId === 3,
        ) ?? []
      );
    } else if (method === Methods.Media_Exchange_96w) {
      return (
        plates?.filter(
          (plate) => plate?.labwareType?.plateLayoutTypeId === 4,
        ) ?? []
      );
    } else if (method === Methods.Lynx_384_Variable_Feed) {
      return (
        plates?.filter(
          (plate) => plate?.labwareType?.plateLayoutTypeId === 7,
        ) ?? []
      );
    }
  }, [plates, method]);

  const handleChange = (
    eventValue: Maybe<
      {
        __typename?: "PlatesCreated";
      } & Pick<
        PlatesCreated,
        "plateCreatedId" | "plateBarcode" | "comments" | "dateCreated"
      >
    >[],
  ) => {
    setFieldValue(
      `feedGroupArray[${index}].plateIds`,
      eventValue.map((v) => v?.plateCreatedId) as number[],
    );

    setFieldValue(`feedGroupArray[${index}].numberOfPlates`, eventValue.length);
  };

  const expandBarcode = (entry: string): string[] => {
    const s = entry.trim();
    if (!s) return [];

    // Matches: PREFIX_<start>-<end> where start/end are digits
    // Example: MON0170_PS_101-108  => prefix="MON0170_PS_", start="101", end="108"
    const m = s.match(/^(.*_)(\d+)-(\d+)$/);
    if (!m) return [s];

    const [, prefix, startStr, endStr] = m;
    const start = Number(startStr);
    const end = Number(endStr);

    if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) {
      // If malformed, just return original input rather than throwing
      return [s];
    }

    // Preserve padding width from the start number (e.g. "001" -> width 3)
    const width = startStr.length;

    const out: string[] = [];
    for (let n = start; n <= end; n++) {
      out.push(prefix + String(n).padStart(width, "0"));
    }
    return out;
  };

  const analyzeText = (value: string) => {
    const entries = value
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    // Expand ranges like *_101-108 into individual barcodes
    const expanded = entries.flatMap(expandBarcode);

    const storedSet = new Set(
      availablePlates?.map((e) => e?.plateBarcode).filter(Boolean) as string[],
    );

    const matches = new Set(expanded.filter((entry) => storedSet.has(entry)));
    const notMatches = new Set(
      expanded.filter((entry) => !storedSet.has(entry)),
    );
    setPlatesNotFound(Array.from(notMatches));

    const ids =
      availablePlates
        ?.filter((e) => e?.plateBarcode && matches.has(e.plateBarcode))
        .map((e) => e?.plateCreatedId)
        .filter((id): id is number => typeof id === "number") ?? [];

    setFieldValue(`feedGroupArray[${index}].plateIds`, ids);
    setFieldValue(`feedGroupArray[${index}].numberOfPlates`, ids.length);
  };
  const selectedPlateIds = value ?? [];

  const selectedPlates = plates.filter((p) =>
    selectedPlateIds.includes(p?.plateCreatedId ?? 0),
  );
  const selectedPlateBarcodes = selectedPlates
    .map((plate) => plate?.plateBarcode)
    .filter((barcode): barcode is string => !!barcode);

  return (
    <Grid container spacing={2} key={index}>
      <Grid item xs={5}>
        <FormControl sx={{ width: "100%" }}>
          <TextField
            id="outlined-textarea"
            label="Paste list of plates"
            multiline
            rows={5}
            maxRows={5}
            onChange={(e) => setTextAreaContent(e.target.value)}
            InputProps={{
              endAdornment: (
                <Tooltip title="Match pasted barcodes to available plates">
                  <IconButton
                    onClick={() => analyzeText(textAreaContent ?? "")}
                  >
                    <CompareArrowsIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            value={textAreaContent}
            disabled={toggleDisabled}
            fullWidth
            sx={{ height: "120" }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={3}>
        <FormControl sx={{ width: "100%" }}>
          <TextField
            id="outlined-textarea"
            label="Plate(s) not found"
            multiline
            disabled
            rows={5}
            maxRows={5}
            value={platesNotFound.join("\n")}
            fullWidth
            sx={{ height: "100%" }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={3.5}>
        <FormControl sx={{ width: "100%", height: "100%" }}>
          <Autocomplete
            sx={{ height: "100%" }}
            multiple
            id="plates-autocomplete"
            getOptionLabel={(option) => option?.plateBarcode ?? ""}
            noOptionsText={"No plates compatible with selected method"}
            isOptionEqualToValue={(opt, val) =>
              (opt?.plateCreatedId ?? 0) === (val?.plateCreatedId ?? 0)
            }
            value={selectedPlates}
            onChange={(e, value) => handleChange(value)}
            renderTags={(value, getTagProps) => (
              <div style={{ height: 92, overflowY: "auto", padding: "0 10px" }}>
                {value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option?.plateCreatedId}
                    label={option?.plateBarcode}
                    style={{ margin: "4px" }}
                  />
                ))}
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plate(s) in feed group"
                error={!!formikErrors}
                helperText={formikErrors}
                fullWidth
              />
            )}
            options={availablePlates ?? []}
          />
        </FormControl>
      </Grid>
      {method === Methods.Media_Exchange_12_24w && (
        <>
          <Grid item xs={8} />
          <Grid item xs={3.5}>
            <GenerateMystRack
              index={index}
              toggleDisabled={toggleDisabled}
              handleBlur={handleBlur}
              value={generateMystRack}
              plateBarcodes={selectedPlateBarcodes}
              plateBarcodesLoaded={
                selectedPlateBarcodes.length === selectedPlateIds.length
              }
              setFieldValue={setFieldValue}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
